/* eslint-disable react/prop-types */
import { Drawer, Form, Button, Input, message, Row, Col, Select, Typography, Grid, Switch, InputNumber, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import { validations } from '../utils/validation'; // Import validations
import PropTypes from 'prop-types';
import { useEffect } from 'react';
const { Title } = Typography;
const { useBreakpoint } = Grid;
const EditCustomerDrawer = ({ open, onClose, customer, onSave, isPending }) => {
  const { t } = useTranslation('users');
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [form] = Form.useForm();
  const freeTierEnabled = Form.useWatch('freeTierEnabled', form);

  useEffect(() => {
    if (open && customer) {
      form.setFieldsValue({
        fname: customer.fname,
        lname: customer.lname,
        phoneNumber: customer.phoneNumber,
        countryCode: customer.countryCode,
        tagId: customer.tagId,
        address: customer.address,
        employeeId: customer.employeeId || '',
        factoryNumber: customer.factoryNumber || '',
        startChargeEnabled: customer.startChargeEnabled !== false,
        freeTierEnabled: !!customer.freeTierEnabled,
        freeTierUnit: customer.freeTierUnit || 'KWH',
        freeTierMonthlyThreshold: customer.freeTierMonthlyThreshold || 0,
        idleFeeEnabled: !!customer.idleFeeEnabled,
      });
    }
  }, [open, customer, form]);

  const handleSave = () => {
    form
      .validateFields()
      .then((values) => {
        onSave({
          ...values,
          startChargeEnabled: values.startChargeEnabled !== false,
          freeTierEnabled: !!values.freeTierEnabled,
          freeTierUnit: values.freeTierUnit || 'KWH',
          freeTierMonthlyThreshold: Number(values.freeTierMonthlyThreshold) || 0,
          idleFeeEnabled: !!values.idleFeeEnabled,
        });
      })
      .catch(() => {
        message.error(t('ValidationFailed'));
      });
  };

  return (
    <Drawer
      maskClosable={false}
      title={<Title level={4} className="!mb-0">{t('EditCustomer')}</Title>}
      width={isMobile ? '100%' : 600}
      onClose={onClose}
      open={open}
      classNames={{ body: { paddingBottom: 80 } }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          fname: customer?.fname,
          lname: customer?.lname,
          phoneNumber: customer?.phoneNumber,
          countryCode: customer?.countryCode,
          tagId: customer?.tagId,
          address: customer?.address,
          employeeId: customer?.employeeId || '',
          factoryNumber: customer?.factoryNumber || '',
          startChargeEnabled: customer?.startChargeEnabled !== false,
          freeTierEnabled: !!customer?.freeTierEnabled,
          freeTierUnit: customer?.freeTierUnit || 'KWH',
          freeTierMonthlyThreshold: customer?.freeTierMonthlyThreshold || 0,
          idleFeeEnabled: !!customer?.idleFeeEnabled,
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="fname"
              label={t('FirstName')}
              rules={[
                validations.required(t('PleaseEnterFirstName')),
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="lname"
              label={t('LastName')}
              rules={[
                validations.required(t('PleaseEnterLastName')),
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="phoneNumber"
              label={t('PhoneNumber')}
              rules={[
                validations.required(t('PleaseEnterPhoneNumber')),
                validations.noSpaces(t('NoWhitespaceAllowed')),
                {
                  pattern: /^\d{7,12}$/,
                  message: t('InvalidPhoneNumber'),
                },
              ]}
            >
              <Input
                disabled
                style={{ height: "38.5px" }}
                addonBefore={
                  <Form.Item
                    name="countryCode"
                    noStyle
                    rules={[
                      validations.required(t('PleaseEnterCountryCode')),
                      {
                        pattern: /^\d{1,4}$/,
                        message: t('InvalidCountryCode'),
                      },
                    ]}
                  >
                    +90
                  </Form.Item>
                }
              />
            </Form.Item>
          </Col>

        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="employeeId"
              label={t('EmployeeId')}
              rules={[validations.noSpaces(t('NoWhitespaceAllowed'))]}
            >
              <Input placeholder={t('EnterEmployeeId')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="factoryNumber"
              label={t('FactoryNumber')}
              rules={[validations.noSpaces(t('NoWhitespaceAllowed'))]}
            >
              <Input placeholder={t('EnterFactoryNumber')} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name="tagId"
          label={t('TagId')}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name={['address', 'line1']}
          label={t('AddressLine1')}
          rules={[validations.noSpaces(t('NoWhitespaceAllowed'))]} // Updated message
        >
          <Input />
        </Form.Item>
        <Form.Item
          name={['address', 'line2']}
          label={t('AddressLine2')}
          rules={[validations.noSpaces(t('NoWhitespaceAllowed'))]}
        >
          <Input />
        </Form.Item>

        <Divider orientation="left">{t('StartCharge')}</Divider>
        <Form.Item
          name="startChargeEnabled"
          label={t('StartChargeEnabled')}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Divider orientation="left">{t('FreeTier')}</Divider>
        <Form.Item
          name="freeTierEnabled"
          label={t('FreeTierEnabled')}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        {freeTierEnabled && (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="freeTierUnit"
                label={t('FreeTierUnit')}
                rules={[validations.required(t('FreeTierUnitRequired'))]}
              >
                <Select
                  options={[
                    { value: 'KWH', label: t('FreeTierUnitKwh') },
                    { value: 'CURRENCY', label: t('FreeTierUnitCurrency') },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="freeTierMonthlyThreshold"
                label={t('FreeTierThreshold')}
                rules={[
                  validations.required(t('FreeTierThresholdRequired')),
                  {
                    type: 'number',
                    min: 0.01,
                    message: t('FreeTierThresholdMin'),
                  },
                ]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        )}

        <Divider orientation="left">{t('IdleFee')}</Divider>
        <Form.Item
          name="idleFeeEnabled"
          label={t('IdleFeeEnabled')}
          valuePropName="checked"
          extra={t('IdleFeeEnabledHelp')}
        >
          <Switch />
        </Form.Item>

        <div className="flex flex-row gap-2">
          <Button type="primary" loading={isPending} onClick={handleSave}>
            {t('Save')}
          </Button>
          <Button onClick={onClose}>{t('Cancel')}</Button>
        </div>
      </Form>
    </Drawer>
  );
};

EditCustomerDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customer: PropTypes.shape({
    fname: PropTypes.string,
    lname: PropTypes.string,
    phoneNumber: PropTypes.string,
    countryCode: PropTypes.string,
    address: PropTypes.object,
    employeeId: PropTypes.string,
    factoryNumber: PropTypes.string,
    startChargeEnabled: PropTypes.bool,
    freeTierEnabled: PropTypes.bool,
    freeTierUnit: PropTypes.string,
    freeTierMonthlyThreshold: PropTypes.number,
    idleFeeEnabled: PropTypes.bool,
  }),
  onSave: PropTypes.func.isRequired,
  isPending: PropTypes.bool,
};

export default EditCustomerDrawer;
